import React, { useState, useEffect } from 'react';

interface ComplianceStandard {
  id: string;
  name: string;
  description: string;
  category: 'product_quality' | 'lab_testing' | 'packaging' | 'documentation' | 'customer_service';
  required: boolean;
  weight: number; // Impact on overall score
}

interface ComplianceCheck {
  id: string;
  standardId: string;
  vendorId: string;
  status: 'compliant' | 'non_compliant' | 'partially_compliant' | 'pending_review';
  score: number;
  maxScore: number;
  lastChecked: string;
  checkedBy: string;
  notes: string;
  evidence?: string[];
  nextReviewDate: string;
  actionItems?: string[];
}

interface VendorCompliance {
  vendorId: string;
  vendorName: string;
  overallComplianceScore: number;
  categoryScores: {
    product_quality: number;
    lab_testing: number;
    packaging: number;
    documentation: number;
    customer_service: number;
  };
  checks: ComplianceCheck[];
  lastAuditDate: string;
  nextAuditDate: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  certifications: string[];
  improvementPlan?: string[];
}

interface QualityComplianceTrackerProps {
  vendorId?: string;
  isStaffView?: boolean;
}

const COMPLIANCE_STANDARDS: ComplianceStandard[] = [
  {
    id: '1',
    name: 'Current Lab Results',
    description: 'All products must have lab results within 6 months',
    category: 'lab_testing',
    required: true,
    weight: 20
  },
  {
    id: '2',
    name: 'Heavy Metals Testing',
    description: 'Products tested for lead, mercury, cadmium, arsenic',
    category: 'lab_testing',
    required: true,
    weight: 15
  },
  {
    id: '3',
    name: 'Microbial Testing',
    description: 'Products tested for harmful bacteria, yeast, mold',
    category: 'lab_testing',
    required: true,
    weight: 15
  },
  {
    id: '4',
    name: 'Accurate Alkaloid Labeling',
    description: 'Alkaloid percentages match lab results within 10%',
    category: 'product_quality',
    required: true,
    weight: 12
  },
  {
    id: '5',
    name: 'Proper Packaging',
    description: 'Child-resistant, tamper-evident, proper labeling',
    category: 'packaging',
    required: true,
    weight: 8
  },
  {
    id: '6',
    name: 'Batch Tracking',
    description: 'Each product lot properly tracked and documented',
    category: 'documentation',
    required: true,
    weight: 10
  },
  {
    id: '7',
    name: 'Customer Service Response',
    description: 'Respond to customer inquiries within 48 hours',
    category: 'customer_service',
    required: false,
    weight: 8
  },
  {
    id: '8',
    name: 'Product Consistency',
    description: 'Products maintain consistent quality across batches',
    category: 'product_quality',
    required: true,
    weight: 12
  }
];

export default function QualityComplianceTracker({ vendorId, isStaffView = false }: QualityComplianceTrackerProps) {
  const [complianceData, setComplianceData] = useState<VendorCompliance | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showImprovementPlan, setShowImprovementPlan] = useState(false);

  useEffect(() => {
    const fetchComplianceData = async () => {
      setLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        const mockChecks: ComplianceCheck[] = [
          {
            id: '1',
            standardId: '1',
            vendorId: vendorId || '1',
            status: 'compliant',
            score: 20,
            maxScore: 20,
            lastChecked: '2024-01-20',
            checkedBy: 'staff-001',
            notes: 'All products have current lab results',
            evidence: ['lab-report-001.pdf', 'lab-report-002.pdf'],
            nextReviewDate: '2024-04-20',
            actionItems: []
          },
          {
            id: '2',
            standardId: '2',
            vendorId: vendorId || '1',
            status: 'compliant',
            score: 15,
            maxScore: 15,
            lastChecked: '2024-01-18',
            checkedBy: 'staff-002',
            notes: 'Heavy metals testing completed for all batches',
            evidence: ['metals-test-001.pdf'],
            nextReviewDate: '2024-04-18',
            actionItems: []
          },
          {
            id: '3',
            standardId: '3',
            vendorId: vendorId || '1',
            status: 'partially_compliant',
            score: 10,
            maxScore: 15,
            lastChecked: '2024-01-15',
            checkedBy: 'staff-001',
            notes: 'Missing microbial testing for 2 recent batches',
            evidence: ['microbial-test-001.pdf'],
            nextReviewDate: '2024-02-15',
            actionItems: ['Submit microbial testing for batches KR-2024-003 and KR-2024-004']
          },
          {
            id: '4',
            standardId: '4',
            vendorId: vendorId || '1',
            status: 'non_compliant',
            score: 6,
            maxScore: 12,
            lastChecked: '2024-01-19',
            checkedBy: 'staff-003',
            notes: 'Alkaloid percentages on White Maeng Da exceed lab results by 15%',
            evidence: [],
            nextReviewDate: '2024-02-05',
            actionItems: ['Correct alkaloid labeling on White Maeng Da products', 'Retest batch for accuracy']
          },
          {
            id: '5',
            standardId: '5',
            vendorId: vendorId || '1',
            status: 'compliant',
            score: 8,
            maxScore: 8,
            lastChecked: '2024-01-17',
            checkedBy: 'staff-002',
            notes: 'All packaging meets requirements',
            evidence: ['packaging-audit-001.pdf'],
            nextReviewDate: '2024-04-17',
            actionItems: []
          }
        ];

        const mockData: VendorCompliance = {
          vendorId: vendorId || '1',
          vendorName: 'Premium Kratom Co',
          overallComplianceScore: 79,
          categoryScores: {
            product_quality: 75,
            lab_testing: 83,
            packaging: 100,
            documentation: 85,
            customer_service: 88
          },
          checks: mockChecks,
          lastAuditDate: '2024-01-15',
          nextAuditDate: '2024-04-15',
          riskLevel: 'medium',
          certifications: ['GMP Certified', 'ISO 9001'],
          improvementPlan: [
            'Complete missing microbial testing for recent batches',
            'Correct alkaloid labeling discrepancies',
            'Implement automated batch tracking system',
            'Establish quarterly compliance reviews'
          ]
        };
        
        setComplianceData(mockData);
        setLoading(false);
      }, 1000);
    };

    fetchComplianceData();
  }, [vendorId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return '#10b981';
      case 'partially_compliant': return '#f59e0b';
      case 'non_compliant': return '#ef4444';
      case 'pending_review': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#f97316';
      case 'critical': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return '#10b981';
    if (percentage >= 80) return '#f59e0b';
    if (percentage >= 70) return '#f97316';
    return '#ef4444';
  };

  const filteredChecks = selectedCategory === 'all' 
    ? complianceData?.checks || []
    : complianceData?.checks.filter(check => {
        const standard = COMPLIANCE_STANDARDS.find(s => s.id === check.standardId);
        return standard?.category === selectedCategory;
      }) || [];

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading compliance data...</p>
      </div>
    );
  }

  if (!complianceData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>No compliance data available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Quality Compliance Tracker
        </h2>
        <p style={{ color: '#6b7280', fontSize: '1rem' }}>
          {complianceData.vendorName} - Comprehensive Quality Standards Assessment
        </p>
      </div>

      {/* Compliance Overview */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: getScoreColor(complianceData.overallComplianceScore, 100), margin: '0' }}>
            {complianceData.overallComplianceScore}%
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Overall Compliance</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: getRiskColor(complianceData.riskLevel), margin: '0', textTransform: 'capitalize' }}>
            {complianceData.riskLevel}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Risk Level</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0891b2', margin: '0' }}>
            {new Date(complianceData.nextAuditDate).toLocaleDateString()}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Next Audit</p>
        </div>
        
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '1.5rem', 
          borderRadius: '0.5rem', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669', margin: '0' }}>
            {complianceData.certifications.length}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>Certifications</p>
        </div>
      </div>

      {/* Category Scores */}
      <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>Category Compliance Scores</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {Object.entries(complianceData.categoryScores).map(([category, score]) => (
            <div key={category} style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#a0aec0', textTransform: 'capitalize' }}>
                  {category.replace('_', ' ')}
                </span>
                <span style={{ fontSize: '1.125rem', fontWeight: 'bold', color: getScoreColor(score, 100) }}>
                  {score}%
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${score}%`, 
                  height: '100%', 
                  backgroundColor: getScoreColor(score, 100),
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Plan */}
      {complianceData.improvementPlan && complianceData.improvementPlan.length > 0 && (
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>Improvement Plan</h3>
            <button
              onClick={() => setShowImprovementPlan(!showImprovementPlan)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              {showImprovementPlan ? 'Hide' : 'Show'} Plan
            </button>
          </div>
          
          {showImprovementPlan && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {complianceData.improvementPlan.map((item, index) => (
                <div key={index} style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#eff6ff', 
                  borderRadius: '0.375rem',
                  borderLeft: '4px solid #3b82f6'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ 
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      borderRadius: '50%',
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      lineHeight: '20px',
                      fontWeight: 'bold'
                    }}>
                      {index + 1}
                    </span>
                    <span style={{ color: '#1e40af', fontSize: '0.875rem' }}>{item}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detailed Compliance Checks */}
      <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>Compliance Checks</h3>
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ 
              padding: '0.5rem', 
              borderRadius: '0.375rem', 
              border: '1px solid #4a5568',
              backgroundColor: '#2d3748',
              fontSize: '0.875rem'
            }}
          >
            <option value="all">All Categories</option>
            <option value="product_quality">Product Quality</option>
            <option value="lab_testing">Lab Testing</option>
            <option value="packaging">Packaging</option>
            <option value="documentation">Documentation</option>
            <option value="customer_service">Customer Service</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredChecks.map(check => {
            const standard = COMPLIANCE_STANDARDS.find(s => s.id === check.standardId);
            return (
              <div key={check.id} style={{ 
                padding: '1.5rem', 
                border: '1px solid #4a5568', 
                borderRadius: '0.5rem',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ flex: '1' }}>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>
                      {standard?.name}
                    </h4>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>
                      {standard?.description}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '0.25rem', 
                        fontSize: '0.75rem', 
                        fontWeight: '600',
                        backgroundColor: getStatusColor(check.status) + '20',
                        color: getStatusColor(check.status),
                        textTransform: 'capitalize'
                      }}>
                        {check.status.replace('_', ' ')}
                      </span>
                      {standard?.required && (
                        <span style={{ 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '0.25rem', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          backgroundColor: '#dc262620',
                          color: '#dc2626'
                        }}>
                          Required
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: getScoreColor(check.score, check.maxScore) }}>
                      {check.score}/{check.maxScore}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Last checked: {new Date(check.lastChecked).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                {check.notes && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ color: '#a0aec0', fontSize: '0.875rem', margin: '0' }}>{check.notes}</p>
                  </div>
                )}
                
                {check.actionItems && check.actionItems.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h5 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.5rem 0' }}>
                      Action Items:
                    </h5>
                    <ul style={{ margin: '0', paddingLeft: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                      {check.actionItems.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', fontSize: '0.75rem', color: '#9ca3af' }}>
                  <span>Checked by: {check.checkedBy}</span>
                  <span>Next review: {new Date(check.nextReviewDate).toLocaleDateString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}